/// <reference types="vite-plugin-svgr/client" />
import React, { useEffect, useState } from 'react';
import styles from './Svg.module.scss';
import useUserStore from '@core/stores/useUserStore';
import { getTenantInfo } from '@shared/getTenantInfo';
import { logger } from '@shared/logger';

type TSVGProps = {
    /**
     * The name of the SVG file (excluding the file extension).
     */
    name: string;
    /**
     * Hover tooltip text.
     */
    title?: string;
    /**
     * Whether to load the SVG as raw HTML or as an image source. Default is true (raw).
     */
    raw?: boolean;
    /**
     * Whether to apply a theme (styling) to the SVG. Default is false.
     */
    themed?: boolean;
    /**
     * Custom class name for the SVG element.
     */
    className?: string;
    /**
     * The UI key to use for the SVG. If not provided, the default UI key will be used.
     * This is useful for loading SVGs from a specific tenant configuration.
     */
    propUiKey?: string | null;
    /**
     * Whether the SVG is an icon (used for applying specific logic). Default is true.
     */
    isIcon?: boolean;
    /**
     * A callback function to handle the click event on the SVG.
     */
    onClick?: (e: React.SyntheticEvent<EventTarget>) => void;
};

/**
 * Svg component dynamically imports and renders an SVG file based on the provided name.
 * The SVG can be rendered either as raw HTML or as an image, with optional theming and click handling.
 *
 * @param {TSVGProps} props - The props to configure the Svg component.
 * @param {string} props.name - The name of the SVG file to render.
 * @param {boolean} [props.raw=true] - Whether to load the SVG as raw HTML or as an image.
 * @param {boolean} [props.themed=false] - Whether to apply a theme to the SVG.
 * @param {string} [props.className=''] - Optional class name to apply to the SVG element.
 * @param {boolean} [props.isIcon=true] - Whether the SVG is used as an icon.
 * @param {function} [props.onClick] - Optional callback function to handle click events.
 *
 * @returns The rendered SVG as raw HTML or an image.
 */
export const Svg = ({
    name,
    title = '',
    className = '',
    raw = true,
    themed = false,
    isIcon = true,
    propUiKey,
    onClick,
}: TSVGProps) => {
    const [svg, setSvg] = useState('');
    const tenantConfig = useUserStore((state) => state.tenantConfig);
    const uiKey = propUiKey || getTenantInfo().uiKey || 'core';
    /**
     * Effect that dynamically imports the SVG file based on the provided properties.
     * It attempts to load the SVG based on the tenant configuration, falling back to core assets if necessary.
     */
    useEffect(() => {
        async function getSVG() {
            // Don't change this dynamic import unless you understand the implications with Vite
            try {
                const { default: importedSVG } = await import(
                    raw
                        ? isIcon
                            ? `@ui/${uiKey}/assets/icons/${name}.svg?raw`
                            : `@ui/${uiKey}/assets/${name}.svg?raw`
                        : isIcon
                          ? `@ui/${uiKey}/assets/icons/${name}.svg`
                          : `@ui/${uiKey}/assets/${name}.svg`
                );

                setSvg(importedSVG);
            } catch (error) {
                // Fallback to core assets if the tenant-specific assets are not found
                logger.info('SVG', 1, 'get SVG from core assets:', name);
                try {
                    const { default: importedSVG } = await import(
                        raw
                            ? isIcon
                                ? `@core/assets/icons/${name}.svg?raw`
                                : `@core/assets/${name}.svg?raw`
                            : isIcon
                              ? `@core/assets/icons/${name}.svg`
                              : `@core/assets/${name}.svg`
                    );

                    setSvg(importedSVG);
                } catch (fallbackError) {
                    // Log the fallback error in case the core assets are also missing, without showing in the console
                    logger.error('SVG', 1, `Fallback failed for ${name}`, fallbackError);
                }
            }
        }

        getSVG();
    }, [isIcon, name, raw, tenantConfig, uiKey]);

    // Apply a class name for theming
    const svgClass = `${className} ${themed ? styles.svg : ''}`;

    /**
     * Handles the click event for the SVG element.
     * If an `onClick` handler is provided, it will be called with the event.
     *
     * @param {React.SyntheticEvent} ev - The click event.
     */
    const handleOnClick = (ev: React.SyntheticEvent) => {
        if (onClick) {
            ev.preventDefault();
            onClick(ev);
        }
    };

    // Render the SVG either as raw HTML or as an image
    if (raw) {
        return (
            <i
                dangerouslySetInnerHTML={{ __html: svg }}
                className={svgClass}
                title={title}
                onClick={handleOnClick}
            />
        );
    } else {
        return <img src={svg} className={svgClass} alt={title} onClick={handleOnClick} />;
    }
};
